import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IdentityCard } from '@/components/IdentityCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore } from '@/store/settings';
import { useUiStore } from '@/store/ui';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { unseenCircleCount } from '@/features/circle/circleSeen';
import { useGroupStore } from '@/store/group';
import { MeshLinkError } from '@/lib/result';
import { AUTO_LOCK_OPTIONS } from '@/config';
import { ROUTE_PATH } from '@/router';
import { loadFavorites, removeFavorite, type FavoriteItem } from '@/utils/favorites';
import { QrCodeView } from '@/components/QrCodeView';
import { Modal } from '@/components/ui/Modal';
import {
  buildInviteUrl,
  consumePendingRef,
  markReferralBound,
  peekPendingRef,
  refToAddress,
  REFERRAL_COPY,
} from '@/utils/referral';
import { NEWCOMER_TASKS, computeTaskDone } from '@/utils/tasks';
import { AvatarChip } from '@/components/AvatarChip';
import { AVATAR_EMOJIS, AVATAR_BGS, loadAvatar, saveAvatar, type AvatarValue } from '@/utils/avatar';
import { useFeedStore } from '@/store/feed';
import {
  getNotifySettings,
  saveNotifySettings,
  requestNotifyPermission,
  type NotifySettings,
} from '@/lib/notify';

/** 「我的」Tab：DID 卡片 + 资料编辑 + 链上状态 + 锁定策略 + 入口导航。 */
export function MePage(): JSX.Element {
  const navigate = useNavigate();
  const { did, peerId, name, bio, registered, faucetClaimed, patch, status } = useIdentityStore();
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);
  const setAutoLockMinutes = useSettingsStore((s) => s.setAutoLockMinutes);
  const lock = useIdentityStore((s) => s.lock);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [followCount, setFollowCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [favs, setFavs] = useState<FavoriteItem[]>(() => loadFavorites());
  const [checkinState, setCheckinState] = useState<{ today: boolean; streak: number }>(() => {
    try {
      const raw = window.localStorage.getItem('mesh_checkin');
      const parsed = raw ? (JSON.parse(raw) as { date: string; streak: number }) : null;
      const today = new Date().toISOString().slice(0, 10);
      if (!parsed) return { today: false, streak: 0 };
      if (parsed.date === today) return { today: true, streak: parsed.streak };
      // 连续判定：昨天签到则递增，否则重置
      const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
      const streak = parsed.date === yesterday ? Math.min(7, parsed.streak + 1) : 1;
      return { today: false, streak };
    } catch {
      return { today: false, streak: 0 };
    }
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRef, setPendingRef] = useState<string | null>(() => peekPendingRef());
  const [binding, setBinding] = useState(false);
  const [contacts] = useState(() => useChatStore.getState().contacts);
  const [groups] = useState(() => useGroupStore.getState().groups);
  /** v3.23 消息通知设置（系统通知 / 提示音 / 朋友圈）。 */
  const [notifySettings, setNotifySettings] = useState<NotifySettings>(() => getNotifySettings());
  const [notifyPerm, setNotifyPerm] = useState<'granted' | 'denied' | 'default' | 'unsupported'>(() =>
    typeof window !== 'undefined' && 'Notification' in window
      ? (Notification.permission as 'granted' | 'denied' | 'default')
      : 'unsupported',
  );
  const toggleNotifySetting = (key: keyof NotifySettings): void => {
    const next = { ...notifySettings, [key]: !notifySettings[key] };
    setNotifySettings(next);
    saveNotifySettings(next);
    useUiStore.getState().pushToast('success', '通知设置已保存');
  };
  const enableSystemNotify = async (): Promise<void> => {
    const perm = await requestNotifyPermission();
    setNotifyPerm(perm);
    if (perm === 'granted') {
      toggleNotifySetting('system');
      useUiStore.getState().pushToast('success', '系统通知已开启');
    } else if (perm === 'denied') {
      useUiStore.getState().pushToast('error', '浏览器已拒绝通知权限，请在浏览器设置中允许');
    } else if (perm === 'unsupported') {
      useUiStore.getState().pushToast('error', '当前浏览器不支持系统通知');
    }
  };
  /** v3.21 朋友圈未读数（入口红点）。 */
  const [circleUnseen] = useState(() => {
    const posts = useFeedStore.getState().posts;
    const following = useSocialStore.getState().following;
    const did = useIdentityStore.getState().did;
    const set = new Set<string>();
    for (const c of useChatStore.getState().contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    if (did) set.add(did.toLowerCase());
    return unseenCircleCount(posts, set);
  });

  // ── v3.19 自媒体：头像 + 关注列表 ──
  const [avatar, setAvatar] = useState<AvatarValue | null>(() => loadAvatar());
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [followingList, setFollowingList] = useState<Array<{ did: string; name: string }>>([]);
  const posts = useFeedStore((s) => s.posts);
  const authorNameOf = (d: string): string => {
    for (const p of posts) {
      if (typeof p.authorDid === 'string' && p.authorDid.toLowerCase() === d.toLowerCase()) {
        const n = typeof p.authorName === 'string' && p.authorName ? p.authorName : '';
        if (n) return n;
      }
    }
    return d.replace(/^did:dpb:/, '').slice(0, 10);
  };

  /** 新手任务完成态（动态检测）。 */
  const taskCtx = {
    unlocked: status === 'unlocked',
    checkinToday: checkinState.today,
    contactCount: contacts.length,
    groupCount: groups.length,
  };
  const taskDone = (id: (typeof NEWCOMER_TASKS)[number]['id']): boolean => computeTaskDone(id, taskCtx);
  const doneCount = NEWCOMER_TASKS.filter((t) => taskDone(t.id)).length;

  /** 点击未完成任务：执行对应动作 / 跳转到对应入口。 */
  const runTask = (id: (typeof NEWCOMER_TASKS)[number]['id']): void => {
    if (taskDone(id)) return;
    switch (id) {
      case 'unlock':
        navigate(ROUTE_PATH.unlock);
        break;
      case 'referral':
        if (pendingRef) void doBindReferral();
        else setInviteOpen(true);
        break;
      case 'checkin':
        void doCheckin();
        break;
      case 'post':
        navigate(ROUTE_PATH.feed);
        break;
      case 'contact':
        navigate(ROUTE_PATH.discover);
        break;
      case 'official':
        navigate(ROUTE_PATH.discover);
        break;
      case 'group':
        navigate(ROUTE_PATH.chat);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    void container.social
      .getFollowing()
      .then((list) => {
        setFollowCount(list.length);
        setFollowingList(
          list.map((d) => ({
            did: d,
            name: authorNameOf(d),
          })),
        );
      })
      .catch(() => setFollowCount(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 每日签到：checkin 上链 → 签到挖矿（首日 0.0005 / 连续 ≥2 天 0.001 DPB）。 */
  const doCheckin = async (): Promise<void> => {
    if (checkingIn || checkinState.today) return;
    const addr = container.identity.address;
    if (!addr) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setCheckingIn(true);
    try {
      await container.chain.checkin({
        from: addr as `0x${string}`,
        ts: Date.now(),
        signature: container.identity.signObject({ checkin: addr, ts: Date.now() }),
      });
      const streak = checkinState.streak === 0 ? 1 : Math.min(7, checkinState.streak + 1);
      try {
        window.localStorage.setItem(
          'mesh_checkin',
          JSON.stringify({ date: new Date().toISOString().slice(0, 10), streak }),
        );
      } catch {
        /* 存储满忽略 */
      }
      setCheckinState({ today: true, streak });
      useUiStore.getState().pushToast('success', `签到成功（连续 ${streak} 天，奖励已上链）`);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setCheckingIn(false);
    }
  };

  /** 绑定邀请人：链上 referral bind（一次永久，自动行为挖矿奖励）。 */
  const doBindReferral = async (): Promise<void> => {
    if (binding) return;
    const addr = container.identity.address;
    if (!addr) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    const ref = consumePendingRef();
    if (!ref) return;
    setBinding(true);
    try {
      const referrer = refToAddress(ref) as `0x${string}`;
      await container.chain.referral({
        from: addr as `0x${string}`,
        referrer,
        signature: container.identity.signObject({ referral: addr, referrer, ts: Date.now() }),
      });
      markReferralBound();
      setPendingRef(null);
      useUiStore.getState().pushToast('success', '已绑定邀请人，推荐奖励已上链');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      // 失败恢复 pending，允许重试
      try {
        window.localStorage.setItem('mesh_pending_ref', ref);
      } catch {
        /* 忽略 */
      }
      setPendingRef(ref);
    } finally {
      setBinding(false);
    }
  };

  const openEdit = (): void => {
    setEditName(name);
    setEditBio(bio ?? '');
    setEditing(true);
  };

  const saveProfile = async (): Promise<void> => {
    setSaving(true);
    try {
      const next = await container.identity.updateProfile({
        name: editName.trim(),
        bio: editBio.trim(),
      });
      patch({ name: next.name, bio: next.bio });
      useUiStore.getState().pushToast('success', '资料已更新');
      setEditing(false);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setSaving(false);
    }
  };

  /** 打开收藏来源会话（v3.13）：单聊 setActive(peerId)，群聊 setActive(groupId)，跳转聊天页。 */
  const openFavorite = (f: FavoriteItem): void => {
    if (f.convKind === 'group') {
      useGroupStore.getState().setActive(f.convId);
      useChatStore.getState().setActive(null);
    } else {
      useChatStore.getState().setActive(f.convId);
      useGroupStore.getState().setActive(null);
    }
    setFavOpen(false);
    navigate(ROUTE_PATH.chat);
  };

  const unfavorite = (key: string): void => {
    setFavs(removeFavorite(key));
    useUiStore.getState().pushToast('success', '已取消收藏');
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <IdentityCard did={did} peerId={peerId} name={name} />

      <div className="card p-5">
        <h2 className="mb-2 text-sm font-medium text-text">
          新手任务 <span className="text-[11px] text-dim">({doneCount}/{NEWCOMER_TASKS.length})</span>
        </h2>
        <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-line">
          <div
            className="h-full rounded-full bg-accent transition-all"
            style={{ width: `${Math.round((doneCount / NEWCOMER_TASKS.length) * 100)}%` }}
          />
        </div>
        <ul className="space-y-1.5">
          {NEWCOMER_TASKS.map((t) => {
            const done = taskDone(t.id);
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => runTask(t.id)}
                  className={`flex w-full items-center gap-2 rounded-btn px-2.5 py-2 text-left text-xs transition-colors ${
                    done ? 'bg-ok/10 text-ok' : 'bg-panel-2 text-text hover:bg-line'
                  }`}
                >
                  <span aria-hidden>{t.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{t.title}</span>
                    <span className="block truncate text-[11px] text-dim">{t.desc}</span>
                  </span>
                  {done ? <span className="shrink-0 text-ok">✓</span> : <span className="shrink-0 text-accent">去完成 ›</span>}
                </button>
              </li>
            );
          })}
        </ul>
        {doneCount === NEWCOMER_TASKS.length ? (
          <p className="mt-3 text-center text-xs text-ok">🎉 新手任务全部完成，开始你的生态探索吧！</p>
        ) : null}
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">链上状态</h2>
        <div className="flex flex-wrap gap-2">
          <Badge tone={registered ? 'ok' : 'warn'}>{registered ? 'DID 已注册' : 'DID 未注册'}</Badge>
          <Badge tone={faucetClaimed ? 'gold' : 'neutral'}>
            {faucetClaimed ? '新手奖励已领取' : '未领取新手奖励'}
          </Badge>
          <button
            type="button"
            title="查看我的关注列表（v3.19 自媒体）"
            className="rounded-btn bg-panel-2 px-2 py-1 text-[11px] text-dim hover:text-accent"
            onClick={() => {
              setFollowOpen(true);
              void container.social
                .getFollowing()
                .then((list) => {
                  setFollowingList(
                    list.map((d) => ({ did: d, name: authorNameOf(d) })),
                  );
                })
                .catch(() => undefined);
            }}
          >
            关注 {followCount} ›
          </button>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">每日签到（签到即挖矿）</h2>
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-dim">
            {checkinState.today ? (
              <span className="text-ok">今日已签到 · 连续 {checkinState.streak} 天 ✓</span>
            ) : (
              <span>
                连续 {checkinState.streak} 天 · 首日 0.0005 DPB，连续奖励 0.001 DPB（7 天封顶）
              </span>
            )}
          </div>
          <Button size="sm" loading={checkingIn} disabled={checkinState.today} onClick={() => void doCheckin()}>
            {checkinState.today ? '已签到 ✓' : '签到'}
          </Button>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">资料</h2>        {editing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <AvatarChip did={did ?? ''} name={name} size="md" />
              <Button variant="ghost" size="sm" onClick={() => setAvatarOpen(true)}>
                更换头像
              </Button>
            </div>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="昵称" />
            <Input value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder="简介" />
            <div className="flex gap-2">
              <Button size="sm" loading={saving} onClick={() => void saveProfile()}>
                保存
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                取消
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3">
              <AvatarChip did={did ?? ''} name={name} size="md" />
              <div className="min-w-0">
                <p className="text-sm text-text">{name || '未设置昵称'}</p>
                <p className="mt-1 text-xs text-dim">{bio || '未填写简介'}</p>
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <Button variant="ghost" size="sm" onClick={openEdit}>
                编辑资料
              </Button>
              <Button variant="ghost" size="sm" onClick={() => did && navigate(`/u/${encodeURIComponent(did)}`)}>
                我的主页
              </Button>
            </div>
          </div>
        )}

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">自动锁定</h2>
        <div className="flex flex-wrap gap-2">
          {AUTO_LOCK_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAutoLockMinutes(m)}
              className={`rounded-btn px-3 py-1.5 text-xs ${
                autoLockMinutes === m ? 'bg-accent text-[#04141c]' : 'bg-panel-2 text-dim hover:bg-line'
              }`}
            >
              {m} 分钟
            </button>
          ))}
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">消息通知</h2>
        <div className="space-y-2 rounded-btn bg-panel-2 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-text">系统通知</p>
              <p className="text-[11px] text-dim">
                新消息/群消息/好友动态系统弹窗
                {notifyPerm === 'granted' ? ' · 已授权' : notifyPerm === 'denied' ? ' · 浏览器已拒绝' : ''}
              </p>
            </div>
            <Button
              size="sm"
              variant={notifySettings.system ? 'primary' : 'ghost'}
              onClick={() => {
                if (notifySettings.system) toggleNotifySetting('system');
                else void enableSystemNotify();
              }}
            >
              {notifySettings.system ? '开启' : '关闭'}
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-text">提示音</p>
              <p className="text-[11px] text-dim">新消息轻提示音</p>
            </div>
            <Button
              size="sm"
              variant={notifySettings.sound ? 'primary' : 'ghost'}
              onClick={() => toggleNotifySetting('sound')}
            >
              {notifySettings.sound ? '开启' : '关闭'}
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-text">朋友圈动态</p>
              <p className="text-[11px] text-dim">好友发布新动态时提醒</p>
            </div>
            <Button
              size="sm"
              variant={notifySettings.circle ? 'primary' : 'ghost'}
              onClick={() => toggleNotifySetting('circle')}
            >
              {notifySettings.circle ? '开启' : '关闭'}
            </Button>
          </div>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">邀请好友</h2>
        <div className="space-y-2">
          {pendingRef ? (
            <div className="rounded-btn border border-accent/30 bg-accent/10 px-3 py-2.5">
              <p className="text-xs text-text">{REFERRAL_COPY.bindDesc}</p>
              <Button size="sm" className="mt-2" loading={binding} onClick={() => void doBindReferral()}>
                {REFERRAL_COPY.bindTitle}
              </Button>
            </div>
          ) : null}
          <Button block size="sm" onClick={() => setInviteOpen(true)}>
            {REFERRAL_COPY.title} · 专属链接 + 二维码
          </Button>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">我的</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => did && navigate(`/u/${encodeURIComponent(did)}`)}>
            我的发布
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/circle')}>
            朋友圈{circleUnseen > 0 ? `（${circleUnseen}）` : ''}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setFavOpen(true)}>
            我的收藏{favs.length ? `（${favs.length}）` : ''}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.explorer)}>
            链浏览器
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.chainWallet)}>
            独立钱包
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.dex)}>
            交易大厅
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.rewards)}>
            挖矿
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.merchant)}>
            商家中心
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.wallet)}>
            钱包
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.settings)}>
            设置
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.security)}>
            安全中心
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.discover)}>
            发现
          </Button>
        </div>

        <div className="mt-6 flex gap-2">
          <Button variant="danger" size="sm" onClick={lock}>
            立即锁定
          </Button>
        </div>
      </div>

      {/* 我的收藏（v3.13）：消息摘要列表，点击跳回来源会话 */}
      <Modal
        open={favOpen}
        onOpenChange={(open) => {
          if (!open) setFavOpen(false);
        }}
        title="我的收藏"
        description="长按聊天消息选择收藏；点击条目跳回来源会话"
      >
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {favs.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-dim">还没有收藏。在聊天里长按一条消息 → 收藏</p>
          ) : (
            favs.map((f) => (
              <div
                key={f.key}
                className="flex items-center gap-2 rounded border border-line bg-panel/60 px-2 py-1.5"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => openFavorite(f)}
                  title="打开来源会话"
                >
                  <span className="block truncate text-xs text-text">
                    <span aria-hidden className="mr-1">
                      {f.kind === 'image' ? '🖼' : f.kind === 'video' ? '🎬' : f.kind === 'audio' ? '🔊' : f.kind === 'file' ? '📄' : '💬'}
                    </span>
                    {f.summary}
                  </span>
                  <span className="block text-[10px] text-dim">
                    {new Date(f.ts).toLocaleString()} · {f.convKind === 'group' ? '群聊' : '好友'}
                  </span>
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded px-2 py-1 text-[10px] text-err hover:bg-panel-2"
                  onClick={() => unfavorite(f.key)}
                >
                  取消
                </button>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* 邀请好友（v3.14）：专属链接二维码 + 奖励说明 */}
      <Modal
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!open) setInviteOpen(false);
        }}
        title={REFERRAL_COPY.title}
        description={REFERRAL_COPY.subtitle}
      >
        <div className="flex flex-col items-center gap-3">
          <QrCodeView value={did ? buildInviteUrl(did) : ''} size={200} label="好友扫码创建身份，自动绑定推荐关系" />
          <p className="w-full rounded-btn bg-panel-2 px-3 py-2 text-xs leading-relaxed text-dim">
            🎁 {REFERRAL_COPY.rewardNew}
            <br />
            🤝 {REFERRAL_COPY.rewardOld}
          </p>
        </div>
      </Modal>

      {/* v3.19 自媒体：头像选择（预置 emoji + 背景色，本机保存；跨端同步待链端头像上链） */}
      <Modal
        open={avatarOpen}
        onOpenChange={(open) => {
          if (!open) setAvatarOpen(false);
        }}
        title="选择头像"
        description="从预置库选择 emoji 与背景色；上链头像后续版本支持"
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-[11px] text-dim">图案</p>
            <div className="grid grid-cols-8 gap-2">
              {AVATAR_EMOJIS.map((em) => (
                <button
                  key={em}
                  type="button"
                  onClick={() =>
                    setAvatar((prev) => {
                      const next = { emoji: em, bg: (prev?.bg ?? AVATAR_BGS[0]) as string };
                      saveAvatar(next);
                      return next;
                    })
                  }
                  className={`flex h-9 items-center justify-center rounded-btn text-lg ${
                    avatar?.emoji === em ? 'bg-accent/20 ring-1 ring-accent' : 'bg-panel-2 hover:bg-line'
                  }`}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-[11px] text-dim">背景色</p>
            <div className="flex gap-2">
              {AVATAR_BGS.map((bg) => (
                <button
                  key={bg}
                  type="button"
                  onClick={() =>
                    setAvatar((prev) => {
                      const next = { emoji: (prev?.emoji ?? AVATAR_EMOJIS[0]) as string, bg };
                      saveAvatar(next);
                      return next;
                    })
                  }
                  className={`h-8 w-8 rounded-full ${avatar?.bg === bg ? 'ring-2 ring-accent ring-offset-1' : ''}`}
                  style={{ backgroundColor: bg }}
                  aria-label={`背景 ${bg}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-btn bg-panel-2 px-3 py-2">
            <AvatarChip did={did ?? ''} name={name} size="md" />
            <p className="text-xs text-dim">头像设置已即时生效（本机保存）</p>
          </div>
          <Button block size="sm" onClick={() => setAvatarOpen(false)}>
            完成
          </Button>
        </div>
      </Modal>

      {/* v3.19 自媒体：我的关注列表（点击进入创作者主页） */}
      <Modal
        open={followOpen}
        onOpenChange={(open) => {
          if (!open) setFollowOpen(false);
        }}
        title={`我的关注（${followCount}）`}
        description="关注/订阅创作者即行为挖矿；点击进入主页"
      >
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {followingList.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-dim">还没有关注任何人。去「发现」或「广场」关注创作者</p>
          ) : (
            followingList.map((f) => (
              <button
                key={f.did}
                type="button"
                className="flex w-full items-center gap-2 rounded border border-line bg-panel/60 px-2 py-1.5 text-left hover:bg-panel-2"
                onClick={() => {
                  setFollowOpen(false);
                  navigate(`/u/${encodeURIComponent(f.did)}`);
                }}
              >
                <AvatarChip did={f.did} name={f.name} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate text-xs text-text">{f.name}</span>
                  <span className="mono block truncate text-[10px] text-dim">{f.did}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
}

export default MePage;
